import React from 'react';
import { Shield, X, Lock, Users, Globe, AlertTriangle, History, Server, Crown } from 'lucide-react';

const SECURITY_ITEMS = [
  {
    icon: Crown,
    color: 'text-teal-600 bg-teal-50',
    title: 'Plan-Gated Access',
    body: 'The vault unlocks on the Growing plan or higher. Every action is re-checked on our servers, so access can never be slipped past from the outside.',
  },
  {
    icon: Users,
    color: 'text-blue-600 bg-blue-50',
    title: 'Family Verification',
    body: 'Before anyone can open, view or download a document, we confirm they are a member of your family. Strangers simply cannot reach your files.',
  },
  {
    icon: Lock,
    color: 'text-amber-600 bg-amber-50',
    title: 'Three Privacy Levels',
    body: 'Each document is Private (only you), Executors Only (you + trusted executors), or All Members. You control exactly who sees what, file by file.',
  },
  {
    icon: Shield,
    color: 'text-indigo-600 bg-indigo-50',
    title: 'Owner → Executor → Member Roles',
    body: 'The family owner manages everything. Executors are appointed by the owner and notified by email. Regular members only see what has been shared with them.',
  },
  {
    icon: AlertTriangle,
    color: 'text-red-600 bg-red-50',
    title: 'Emergency Access Safeguard',
    body: 'If the owner is ever unavailable, an executor can request access. The owner is alerted instantly with reminders at 24h and 48h, and if there is no response, access is granted after 72 hours — and can always be revoked.',
  },
  {
    icon: History,
    color: 'text-purple-600 bg-purple-50',
    title: 'Tamper-Evident Audit Trail',
    body: 'Every upload, view, download, edit and access change is permanently logged with who, when and from where. Only the owner can review this history.',
  },
  {
    icon: Server,
    color: 'text-emerald-600 bg-emerald-50',
    title: 'Durable, Isolated Storage',
    body: 'Files are stored in secure cloud storage, kept separate per family, limited to trusted file types up to 25MB. There are no public links — every download is permission-checked.',
  },
];

export function VaultSecurityModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="vault-security-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-teal-600" />
            How Your Vault Keeps Documents Safe
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="close-vault-security-modal"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="p-6 overflow-y-auto space-y-5">
          <p className="text-sm text-gray-600">
            Your family's most important documents deserve bank-grade protection. Here is exactly how
            the Legacy Forge vault safeguards them.
          </p>
          {SECURITY_ITEMS.map((item) => (
            <div key={item.title} className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${item.color}`}>
                <item.icon className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-sm">{item.title}</h4>
                <p className="text-sm text-gray-600 mt-0.5 leading-relaxed">{item.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
            data-testid="vault-security-got-it-btn"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
